import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button } from "@/comn/components"; // 화면 구성 컴포넌트
import { useForm, useFetch, useResource, useGrid, useModal, useStore, useToast, usePage } from "@/comn/hooks"; // hook
import {
    APIS,
    URLS,
    SF_BTCH_EXCUTN_RSLT_SEARCH_FORM,
    SG_BTCH_EXCUTN_RSLT_LIST,
} from "@/tz/go/egaz/osci/ptl/btch/excutnrslt/services/OsciBtchExcutnRsltService";
import { useEffect } from "react";

/*
 * Managing batch runs list
 * !== 배치실행관리 목록 ==!
 */
export const OSCI0112011Q = (props: any) => {
    useResource({
        defaultSchema: [{ area: "bsopClsfCd" }, { area: "comnCd", comnCd: "OSC0088" }],
    });

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0003"],
    });
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { t } = useTranslation();
    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Managing batch runs Form !== 배치실행결과 Form ==!
        btchExcutnRsltSrch: useForm({
            defaultSchema: SF_BTCH_EXCUTN_RSLT_SEARCH_FORM,
            defaultValues: {
                ...pgeStore?.form,
                startTimeStart: comnUtils.getDate({ m: -1 }),
                startTimeEnd: comnUtils.getDate(),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        btchExcutnRsltSrchRsltList: useGrid({
            defaultSchema: SG_BTCH_EXCUTN_RSLT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // !== 배치 실행관리 목록을 조회한다.==!
        getBtchExcutnRsltList: useFetch({
            api: (page = grid.btchExcutnRsltSrchRsltList.page) =>
                APIS.getBtchExcutnRsltList(
                    form.btchExcutnRsltSrch.getValues(),
                    page,
                    grid.btchExcutnRsltSrchRsltList.size,
                ),
            enabled: !comnUtils.isEmpty(form.btchExcutnRsltSrch.getValue("bsopClsfCd")),
            key: [grid.btchExcutnRsltSrchRsltList.page, grid.btchExcutnRsltSrchRsltList.size],
            onSuccess: (data) => {
                setStore(pgeUid, {
                    form: form.btchExcutnRsltSrch.getValues(),
                    page: grid.btchExcutnRsltSrchRsltList.page,
                    size: grid.btchExcutnRsltSrchRsltList.size,
                });

                const arrBtchExcutnRslt = data?.btchExcutnRsltList?.content;

                // Set the searched business classification code in the row. !== 검색한 업무분류코드를 행에 세팅한다. ==!
                if (!comnUtils.isEmpty(arrBtchExcutnRslt)) {
                    const objFormData = form.btchExcutnRsltSrch.getValues();
                    const bsopClsfCd = objFormData.bsopClsfCd;

                    for (let i = 0; i < arrBtchExcutnRslt.length; i++) {
                        arrBtchExcutnRslt[i].bsopClsfCd = bsopClsfCd;
                    }

                    grid.btchExcutnRsltSrchRsltList.setData(comnUtils.getGridData(arrBtchExcutnRslt));
                }
            },
            onError: (error) => {
                const { errorCode, errorMessage } = error.response.data;

                // BizException
                if (errorCode === "-999") {
                    modal.openModal({ content: errorMessage });
                } else {
                    // Failed to select. !== 조회가 실패하였습니다. ==!
                    toast.showToast({ type: "error", content: "msg.osc.00043" });
                }

                // grid.btchExcutnRsltSrchRsltList.clearData();
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        getBtchExcutnRsltList: () => {
            form.btchExcutnRsltSrch.handleSubmit(
                () => {
                    const startTimeStart = form.btchExcutnRsltSrch.getValue("startTimeStart");
                    const startTimeEnd = form.btchExcutnRsltSrch.getValue("startTimeEnd");

                    // If the start date is greater, return !== 시작일자가 더 큰 경우 return ==!
                    if (!comnUtils.isEmpty(startTimeStart) && !comnUtils.isEmpty(startTimeEnd)) {
                        const startDate = new Date(startTimeStart);
                        const endDate = new Date(startTimeEnd);

                        if (startDate > endDate) {
                            toast.showToast({ type: "warning", content: "msg.osc.00003" }); // 데이터가 유효하지 않습니다.
                            return;
                        }
                    }

                    grid.btchExcutnRsltSrchRsltList.setPage(0);
                    fetch.getBtchExcutnRsltList.fetch(0);
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // 데이터가 유효하지 않습니다.
                },
            )();
        },
    };

    const parseDate = (dateString: string) => {
        // Try to parse the date string in the format "YYYY-MM-DD HH:MM:SS.ssssss"
        let date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            return date;
        }

        // Try to parse the date string in the format "DD/MM/YYYY HH:MM:SS"
        const parts = dateString.split(/[\s/:]+/);
        if (parts.length === 6) {
            const [day, month, year, hours, minutes, seconds] = parts.map(Number);
            date = new Date(year, month - 1, day, hours, minutes, seconds);
            if (!isNaN(date.getTime())) {
                return date;
            }
        }

        throw new Error(`Invalid date format: ${dateString}`);
    };

    const formatDate = (dateString: string) => {
        const date = parseDate(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");

        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        grid_BtchExctnInfoSrchList: {
            cell: {
                jobName: (props: any) => {
                    const { rowValues, value } = props;
                    return (
                        <Link
                            to={`${URLS.osci0112012q}/${rowValues.jobExecutionId}?bsopClsfCd=${rowValues.bsopClsfCd}`}
                        >
                            {value}
                        </Link>
                    );
                },
                startTime: (props: any) => {
                    const { value } = props;
                    return formatDate(value);
                },
                endTime: (props: any) => {
                    const { value } = props;
                    return formatDate(value);
                },
                duringTime: (props: any) => {
                    const { rowValues } = props;

                    const formatTimeUnit = (unit: number, hasHigherUnit: boolean, isLastUnit: boolean) => {
                        if (unit === 0 && !hasHigherUnit) return "";
                        if (unit === 0 && hasHigherUnit) return "00";
                        // 초 단독 표시는 유지하고, 상위 시간 단위와 함께 표시할 때는 두 자리로 맞춘다.
                        return isLastUnit && !hasHigherUnit ? unit.toString() : unit.toString().padStart(2, "0");
                    };

                    const formatTime = (days: number, hours: number, minutes: number, seconds: number) => {
                        const formattedDays = days ? `${days} ${t("L_DAYS")} ` : "";
                        const formattedHours = formatTimeUnit(hours, !!days, false);
                        const formattedMinutes = formatTimeUnit(minutes, !!days || !!hours, false);
                        const formattedSeconds = formatTimeUnit(seconds, !!days || !!hours || !!minutes, true);

                        const timeParts = [formattedHours, formattedMinutes, formattedSeconds].filter(
                            (part) => part !== "",
                        );
                        const formattedTime = timeParts.length > 0 ? timeParts.join(":") : "";

                        return `${formattedDays}${formattedDays && formattedTime ? " " : ""}${formattedTime}${
                            seconds && !days && !hours && !minutes ? ` ${t("L_SS")}` : ""
                        }`;
                    };

                    return rowValues.endTime ? (
                        <span>
                            {rowValues.seconds !== undefined
                                ? formatTime(rowValues.days, rowValues.hours, rowValues.minutes, rowValues.seconds)
                                : (() => {
                                      const startTime = parseDate(rowValues.startTime);
                                      const endTime = parseDate(rowValues.endTime);
                                      const diffInSeconds = (endTime.getTime() - startTime.getTime()) / 1000;
                                      const days = Math.floor(diffInSeconds / (3600 * 24));
                                      const hours = Math.floor((diffInSeconds % (3600 * 24)) / 3600);
                                      const minutes = Math.floor((diffInSeconds % 3600) / 60);
                                      const seconds = Math.floor(diffInSeconds % 60);
                                      return formatTime(days, hours, minutes, seconds);
                                  })()}
                        </span>
                    ) : null;
                },
            },
        },
    };

    useEffect(() => {
        // Service Category 는 고정값이므로 수정할 수 없도록 한다.
        form.btchExcutnRsltSrch.setEditable("bsopClsfCd", false);
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.btchExcutnRsltSrch.schema.bsopClsfCd} />
                            <Group.Control {...form.btchExcutnRsltSrch.schema.status} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.btchExcutnRsltSrch.schema.jobName}
                                onEnter={handler.getBtchExcutnRsltList}
                            />
                            <Group.Control {...form.btchExcutnRsltSrch.schema.startTimeRnge} />
                        </Group.Row>
                    </Group.Section>

                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role={"reset"}
                                onClick={() => {
                                    form.btchExcutnRsltSrch.reset();
                                    form.btchExcutnRsltSrch.setValues({
                                        startTimeStart: comnUtils.getDate({ m: -1 }),
                                        startTimeEnd: comnUtils.getDate(),
                                        bsopClsfCd: "OSC",
                                        jobName: "",
                                        status: "",
                                    });
                                }}
                            />
                        </Layout.Left>
                        <Layout.Right>
                            <Button role={"search"} onClick={handler.getBtchExcutnRsltList} />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.btchExcutnRsltSrchRsltList.grid}
                            data={fetch.getBtchExcutnRsltList.data?.btchExcutnRsltList}
                            render={render.grid_BtchExctnInfoSrchList}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
        </Page>
    );
};
